/**
 * Aggregator der modularen Admin-Übersetzungen.
 *
 * Jedes Modul exportiert `{ de: {...}, es: {...} }`, wobei der innere Baum ein
 * partieller i18next-translation-Baum ist (top-level Namespaces wie `dashboard`,
 * `appointments`, …). Hier werden alle Module pro Sprache zu einem Baum gemerged
 * und in src/i18n/index.ts in die bestehenden Ressourcen eingefügt.
 *
 * Neue Übersetzungen werden in DE + ES gepflegt. Beim Hinzufügen einer neuen Seite
 * einfach ein Modul anlegen/erweitern und unten in `modules` registrieren.
 */
import { nav } from './nav';
import { common } from './common';
import { dashboard } from './dashboard';
import { calendar } from './calendar';
import { appointments } from './appointments';
import { reminders } from './reminders';
import { recalls } from './recalls';
import { patients } from './patients';
import { intake } from './intake';
import { billing } from './billing';
import { reports } from './reports';
import { eventLog } from './eventLog';
import { settings } from './settings';
import { branding } from './branding';
import { masters } from './masters';
import { templates } from './templates';
import { notes } from './notes';
import { crm } from './crm';
import { help } from './help';
import { users } from './users';
import { googleCalendar } from './googleCalendar';
import { login } from './login';

type Tree = Record<string, any>;
type Module = { de: Tree; es: Tree };

const modules: Module[] = [
  nav,
  common,
  dashboard,
  calendar,
  appointments,
  reminders,
  recalls,
  patients,
  intake,
  billing,
  reports,
  eventLog,
  settings,
  branding,
  masters,
  templates,
  notes,
  crm,
  help,
  users,
  googleCalendar,
  login,
];

/** Rekursives, nicht-mutierendes Merge zweier verschachtelter Objekte (source gewinnt). */
export const deepMerge = (target: Tree, source: Tree): Tree => {
  const out: Tree = { ...target };
  for (const key of Object.keys(source)) {
    const sv = source[key];
    const tv = out[key];
    if (
      sv && typeof sv === 'object' && !Array.isArray(sv) &&
      tv && typeof tv === 'object' && !Array.isArray(tv)
    ) {
      out[key] = deepMerge(tv, sv);
    } else {
      out[key] = sv;
    }
  }
  return out;
};

/** Baut den gemergten Admin-Übersetzungsbaum für eine Sprache. */
export const buildAdminTree = (lang: 'de' | 'es'): Tree =>
  modules.reduce<Tree>((acc, mod) => deepMerge(acc, mod[lang] || {}), {});
